import { businessDescriptionOptions } from 'screens/LoginOrSignup/components/CompanyData/utils'
import { RegistrationStepProps } from 'screens/Registration/types'
import Styles from './BusinessType.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelectionHandler } from 'screens/Registration/hooks/useSelectionHandler'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import CardButton from 'blaze-ui/CardButton/CardButton'
import { useCallback, useState, useRef, useEffect } from 'react'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { Item } from '@react-stately/collections'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import client from 'api/client'
import { useUpdateRegistrationData } from 'hooks/queries/useUpdateRegistrationData'
import { Drawer } from 'components/Drawer'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFoldersAndFiles } from 'hooks/queries/useFoldersAndFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { collectResults } from 'utils/query'
import { AlmanacFile } from 'types'
import { useUpdateAlmanacFile } from 'hooks/queries/almanacFiles/useUpdateAlmanacFile'

const CustomBusinessTypeDrawer = ({
  open,
  onClose,
  customBusinessType,
  setCustomBusinessType,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  customBusinessType: string
  setCustomBusinessType: (value: string) => void
  onConfirm: () => void
}) => (
  <Drawer.Root open={open} onClose={onClose}>
    <Drawer.Portal>
      <Drawer.Overlay withBlur />
      <Drawer.Content>
        <Drawer.InnerContent>
          <Drawer.Top>
            <div />
            <Drawer.Title asChild>
              <Text variant="primary">Selected "Other"</Text>
            </Drawer.Title>
            <Drawer.Close label="Cancel" variant="ghost" position="right" />
          </Drawer.Top>
          <Drawer.Container padding="15px">
            <div className={Styles.drawerContent}>
              <label htmlFor="custom-business-type-mobile">
                <Text variant="label" color="var(--dark-60)">
                  Describe your business category
                </Text>
              </label>
              <Input
                id="custom-business-type-mobile"
                name="customBusinessType"
                value={customBusinessType}
                onChange={(e) => setCustomBusinessType((e.target as HTMLInputElement).value)}
                size="xl"
                placeholder="Enter your business category"
                autoFocus
              />
              <Button size="xl" variant="primary" fullWidth isDisabled={!customBusinessType.trim()} onPress={onConfirm}>
                Continue
              </Button>
            </div>
          </Drawer.Container>
        </Drawer.InnerContent>
      </Drawer.Content>
    </Drawer.Portal>
  </Drawer.Root>
)

export const BusinessType: React.FC<RegistrationStepProps> = ({ onNext, onBack }) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const currentWorkspace = useCurrentWorkspace()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [customBusinessType, setCustomBusinessType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { trackBusinessTypeSelected } = useOnboardingTracking()
  const { mutateAsync: updateRegistrationData } = useUpdateRegistrationData()
  const customInputRef = useRef<HTMLDivElement>(null)

  const folders = useFoldersAndFiles(currentWorkspaceId)
  const foldersData = collectResults(folders, (data) => data.files as AlmanacFile[])
  const defaultFolder = foldersData.find((folder) => !!folder.default_project)
  const { mutateAsync: updateDefaultFolder } = useUpdateAlmanacFile({ key: defaultFolder?.id || '' })

  const { selectedValue, handleSelection: originalHandleSelection } = useSelectionHandler({
    paramName: 'businessType',
    onNext: isMobile ? () => {} : onNext,
    dataKey: 'businessType',
  })

  const isOtherSelected = selectedValue === 'Other'

  const handleSelection = useCallback(
    (value: string) => {
      if (value === 'Other' && isMobile) {
        originalHandleSelection('Other')
        setIsDrawerOpen(true)
      } else {
        originalHandleSelection(value)
      }
    },
    [isMobile, originalHandleSelection],
  )

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false)
    setCustomBusinessType('')
    originalHandleSelection('')
  }, [originalHandleSelection])

  const handleDrawerConfirm = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isOtherSelected || !customInputRef.current || isMobile) return

    requestAnimationFrame(() => {
      const candidates = [
        document.querySelector<HTMLElement>('main'),
        customInputRef.current?.closest<HTMLElement>('[style*="overflow"]'),
        customInputRef.current?.closest<HTMLElement>('[class*="root"]'),
        document.documentElement,
      ]

      const scrollContainer = candidates.find((el) => el && el.scrollHeight > el.clientHeight)
      if (!scrollContainer) return

      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' })
      setTimeout(() => customInputRef.current?.querySelector<HTMLInputElement>('input')?.focus(), 200)
    })
  }, [isOtherSelected, isMobile])

  const handleNextWithTracking = useCallback(async () => {
    setIsLoading(true)

    const effectiveBusinessType =
      isOtherSelected && customBusinessType.trim() ? customBusinessType.trim() : selectedValue

    if (!isMobile && effectiveBusinessType) {
      trackBusinessTypeSelected(String(effectiveBusinessType))
    }

    // Update user profile, workspace name, and default folder to use the Full Name provided by the user
    if (name) {
      const [first_name, ...rest] = name.split(' ')
      client.updateUserProfile({ first_name, last_name: rest.join(' ') })

      if (currentWorkspace) {
        const lastName = rest.length > 0 ? rest.join(' ') : ''
        let workspaceName = `${first_name}'s Workspace`
        if (lastName) {
          workspaceName = `${first_name} ${lastName}'s Workspace`
        }

        client.updateWorkspaceSettings(
          {
            workspace: {
              name: workspaceName,
            },
          },
          currentWorkspace?.key,
        )
      }

      if (defaultFolder) {
        updateDefaultFolder({
          title: `${first_name}'s Documents`,
        })
      }
    }

    if (effectiveBusinessType) {
      await updateRegistrationData({ businessType: String(effectiveBusinessType) })
    }

    onNext?.()
    setIsLoading(false)
  }, [
    isOtherSelected,
    customBusinessType,
    selectedValue,
    isMobile,
    name,
    onNext,
    trackBusinessTypeSelected,
    currentWorkspace,
    defaultFolder,
    updateDefaultFolder,
    updateRegistrationData,
  ])

  if (isOnboardingRedesign) {
    const isNextDisabled =
      !selectedValue || isLoading || !name.length || (isOtherSelected && !customBusinessType.trim())

    return (
      <MultiStepStepLayout
        title="Tell us a little bit about yourself"
        isNextButtonDisabled={isNextDisabled}
        onNext={isMobile ? onNext : handleNextWithTracking}
        scrollContainerClassName={Styles.container}
        centeredFooter
        withArrowRight={false}
      >
        <div className={Styles.fieldsContainer}>
          <div className={Styles.fieldGroup}>
            <label htmlFor="name">
              <Text variant="label" color="var(--dark-60)">
                Your full name
              </Text>
            </label>
            <Input
              id="name"
              name="name"
              value={name}
              onChange={(e) => {
                setName((e.target as HTMLInputElement).value)
                setNameError('')
              }}
              size="xl"
              placeholder="Enter your name..."
              autoFocus
            />
            {nameError && (
              <div className={Styles.error}>
                <FormErrorMessage className={Styles.errorMessage}>{nameError}</FormErrorMessage>
              </div>
            )}
          </div>
          <div className={Styles.fieldGroup}>
            <Text variant="label" color="var(--dark-60)">
              Your business category
            </Text>
            <MenuTrigger placement="bottom" popoverClassName={Styles.businessTypePopover}>
              <Pressable>
                <div className={Styles.selectCategory}>
                  {selectedValue ? (
                    <div className={Styles.menuItem}>
                      {businessDescriptionOptions.find((option) => option.title === selectedValue)?.icon}
                      <Text>{selectedValue}</Text>
                    </div>
                  ) : (
                    <Text color="var(--dark-40)">Select a category</Text>
                  )}
                  <ChevronDown2 size={16} />
                </div>
              </Pressable>
              <Menu
                onAction={(key) => handleSelection(String(key))}
                aria-label="Business type"
                selectionMode="single"
                selectedKeys={[selectedValue]}
              >
                {businessDescriptionOptions.map((option) => (
                  <Item key={option.title} textValue={option.title}>
                    <div className={Styles.menuItem}>
                      {option.icon}
                      <Text variant="secondary">{option.title}</Text>
                    </div>
                  </Item>
                ))}
              </Menu>
            </MenuTrigger>
          </div>
          {isOtherSelected && (
            <div className={Styles.fieldGroup} ref={customInputRef}>
              <Text variant="label" color="var(--dark-60)">
                Describe your business category
              </Text>
              <Input
                id="custom-business-type"
                name="customBusinessType"
                value={customBusinessType}
                onChange={(e) => setCustomBusinessType((e.target as HTMLInputElement).value)}
                size="xl"
                placeholder="Enter your business category"
                autoFocus
              />
            </div>
          )}
        </div>
      </MultiStepStepLayout>
    )
  }

  const isNextDisabledLegacy = !selectedValue || (isOtherSelected && !customBusinessType.trim())

  return (
    <MultiStepStepLayout
      title="Choose your business category"
      isNextButtonDisabled={isNextDisabledLegacy}
      onNext={isMobile ? onNext : handleNextWithTracking}
      onBack={onBack}
      scrollContainerClassName={Styles.container}
    >
      <div className={Styles.options}>
        <ResponsiveDesign.Desktop>
          {businessDescriptionOptions.map((option) => (
            <CardButton
              className={Styles.cardButton}
              key={option.title}
              subLabel={option.description}
              label={option.title}
              variant="list"
              onPress={() => handleSelection(option.title)}
              isSelected={selectedValue === option.title}
              media={<span className={Styles.icon}>{option.icon}</span>}
            />
          ))}
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          {businessDescriptionOptions.map((option) => (
            <CardButton
              className={Styles.cardButton}
              key={option.title}
              label={option.title}
              variant="list"
              onPress={() => handleSelection(option.title)}
              isSelected={selectedValue === option.title}
              media={<span className={Styles.icon}>{option.icon}</span>}
              labelClassName={Styles.cardButtonLabel}
            />
          ))}
        </ResponsiveDesign.Mobile>
      </div>
      <ResponsiveDesign.Desktop>
        {isOtherSelected && (
          <div className={Styles.customInputContainer} ref={customInputRef}>
            <label htmlFor="custom-business-type-legacy">
              <Text variant="label" color="var(--dark-60)">
                Describe your business category
              </Text>
            </label>
            <Input
              id="custom-business-type-legacy"
              name="customBusinessType"
              value={customBusinessType}
              onChange={(e) => setCustomBusinessType((e.target as HTMLInputElement).value)}
              size="lg"
              placeholder="Enter your business category"
            />
          </div>
        )}
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <CustomBusinessTypeDrawer
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          customBusinessType={customBusinessType}
          setCustomBusinessType={setCustomBusinessType}
          onConfirm={handleDrawerConfirm}
        />
      </ResponsiveDesign.Mobile>
    </MultiStepStepLayout>
  )
}
